import { AlertTriangle, Check, Copy, ExternalLink, FileText, FolderOpen, MoreHorizontal } from "lucide-react"
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { openPathInNativeApp, revealPathInFileManager } from "../electronAPIs/shell"
import { getFileManagerName } from "../nestAPIs/platform"
import { copyTextToClipboard } from "../utils/clipboard"
import { useNativeHostPathActionsAvailable } from "./utils/nativeHostPathActions"
import { relativePathWithin } from "./utils/pathReference"
import { getFileName } from "./utils/paths"
import { ContextMenu, Menu, type MenuItem, type MenuSection } from "./ui"

export function parentDirectoryPath(path: string): string {
    const trimmed = path.length > 1 ? path.replace(/[/\\]+$/, "") : path
    const separatorIndex = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"))
    if (separatorIndex < 0) return "."
    if (separatorIndex === 0) return trimmed.slice(0, 1)
    if (/^[A-Za-z]:$/.test(trimmed.slice(0, separatorIndex))) return trimmed.slice(0, separatorIndex + 1)
    return trimmed.slice(0, separatorIndex)
}

export function filePathCopyValues(path: string, workingDir: string, isDirectory = false) {
    return {
        absolutePath: path,
        relativePath: relativePathWithin(workingDir, path),
        directoryPath: isDirectory ? path : parentDirectoryPath(path),
        name: getFileName(path),
    }
}

interface FilePathActionTarget {
    path: string
    workingDir: string
    isDirectory?: boolean
}

interface FilePathActionsProps extends FilePathActionTarget {
    compact?: boolean
    className?: string
}

function ActionLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
    return (
        <div className="flex items-center gap-2">
            {icon}
            <span>{children}</span>
        </div>
    )
}

function useFilePathActionMenu({ path, workingDir, isDirectory = false }: FilePathActionTarget): {
    copyState: "idle" | "copied" | "error"
    sections: MenuSection[]
} {
    const [copyFeedback, setCopyFeedback] = useState<{ path: string; state: "copied" | "error" } | null>(null)
    const copyState = copyFeedback?.path === path ? copyFeedback.state : "idle"
    const values = filePathCopyValues(path, workingDir, isDirectory)
    const relativePath = values.relativePath
    const nativePathActionsAvailable = useNativeHostPathActionsAvailable()

    useEffect(() => {
        if (!copyFeedback) return
        const timer = window.setTimeout(() => setCopyFeedback(null), 1_500)
        return () => window.clearTimeout(timer)
    }, [copyFeedback])

    const copy = useCallback(
        async (value: string) => {
            try {
                await copyTextToClipboard(value)
                setCopyFeedback({ path, state: "copied" })
            } catch (error) {
                console.error("[FilePathActions] Failed to copy path:", error)
                setCopyFeedback({ path, state: "error" })
            }
        },
        [path]
    )

    const copyItems = useMemo<MenuItem[]>(
        () => [
            {
                id: "copy-absolute-path",
                label: <ActionLabel icon={<Copy size={15} />}>Copy absolute path</ActionLabel>,
                onSelect: () => copy(values.absolutePath),
            },
            {
                id: "copy-relative-path",
                label: <ActionLabel icon={<Copy size={15} />}>Copy relative path</ActionLabel>,
                disabled: relativePath === null,
                onSelect: relativePath === null ? undefined : () => copy(relativePath),
            },
            {
                id: "copy-directory-path",
                label: <ActionLabel icon={<FolderOpen size={15} />}>Copy directory path</ActionLabel>,
                onSelect: () => copy(values.directoryPath),
            },
            {
                id: "copy-name",
                label: <ActionLabel icon={<FileText size={15} />}>Copy name</ActionLabel>,
                onSelect: () => copy(values.name),
            },
        ],
        [copy, relativePath, values.absolutePath, values.directoryPath, values.name]
    )

    const nativeItems = useMemo<MenuItem[]>(() => {
        if (!nativePathActionsAvailable) return []
        const fileManagerName = getFileManagerName()
        return [
            {
                id: "reveal-in-file-manager",
                label: <ActionLabel icon={<FolderOpen size={15} />}>{`Reveal in ${fileManagerName}`}</ActionLabel>,
                onSelect: () => revealPathInFileManager(path, values.directoryPath),
            },
            ...(!isDirectory
                ? [
                      {
                          id: "open-in-default-app",
                          label: <ActionLabel icon={<ExternalLink size={15} />}>Open in default app</ActionLabel>,
                          onSelect: () => openPathInNativeApp(path),
                      } satisfies MenuItem,
                  ]
                : []),
        ]
    }, [isDirectory, nativePathActionsAvailable, path, values.directoryPath])

    return {
        copyState,
        sections: [{ items: copyItems }, ...(nativeItems.length > 0 ? [{ items: nativeItems }] : [])],
    }
}

function CopyErrorStatus({ copyState }: { copyState: "idle" | "copied" | "error" }) {
    return copyState === "error" ? (
        <span className="sr-only" role="status">
            Copy failed. Try again or copy the path manually.
        </span>
    ) : null
}

export function FilePathActions({ path, workingDir, isDirectory = false, compact = false, className }: FilePathActionsProps) {
    const { copyState, sections } = useFilePathActionMenu({ path, workingDir, isDirectory })
    const label = copyState === "error" ? `Copy failed for ${path}` : `Path actions for ${path}`
    const trigger =
        copyState === "copied" ? (
            <Check size={15} className="text-success" />
        ) : copyState === "error" ? (
            <AlertTriangle size={15} className="text-error" />
        ) : (
            <MoreHorizontal size={16} />
        )

    return (
        <span className="relative shrink-0" data-copy-state={copyState} onKeyDown={(event) => event.stopPropagation()}>
            <Menu
                trigger={trigger}
                triggerAriaLabel={label}
                triggerTitle={copyState === "error" ? "Copy failed" : undefined}
                sections={sections}
                side="bottom"
                align="end"
                sideOffset={4}
                className={{
                    trigger: `${compact ? "!h-10 !w-10" : "!h-8 !w-8"} !shrink-0 !rounded !border-0 !bg-transparent !p-0 text-muted hover:!bg-base-300 hover:text-base-content ${className ?? ""}`,
                    popup: "min-w-56",
                }}
            />
            <CopyErrorStatus copyState={copyState} />
        </span>
    )
}

interface FilePathContextMenuProps extends FilePathActionTarget {
    children: ReactNode
}

export function FilePathContextMenu({ path, workingDir, isDirectory = false, children }: FilePathContextMenuProps) {
    const { copyState, sections } = useFilePathActionMenu({ path, workingDir, isDirectory })

    return (
        <>
            <ContextMenu trigger={children} sections={sections} className={{ trigger: "h-full w-full", popup: "min-w-56" }} />
            <CopyErrorStatus copyState={copyState} />
        </>
    )
}
